/**
 * DOCUMENTATION:
 * Component: CleanCodeProject
 * Purpose: A capstone project demonstrating a full refactor from "Dirty" to "Clean" code.
 * * * Note for Junior Developers:
 * - Refactoring is the process of changing a software system in such a way that it
 * does not alter the external behavior of the code yet improves its internal structure.
 * - This project shows a typical "Order Processor" script.
 * - We move from a "Procedural" style (one long block) to a "Declarative" style (readable steps).
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
    Divider,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import EngineeringIcon from '@mui/icons-material/Engineering';

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1516116216624-53e697fedbea";

/* ✅ Styled Components */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")(({ color }) => ({
    backgroundColor: "#0d1117",
    color: color || "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.85rem",
    lineHeight: 1.5,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', monospace",
}));

/* ✅ Main Component */
export default function CleanCodeProject() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [copiedIndex, setCopiedIndex] = useState(null);

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const refactorSteps = useMemo(() => [
        { step: "1. Identify the 'Smell'", desc: "The dirty code is a 'God Function'—it handles validation, math, and logging in one block with cryptic variable names." },
        { step: "2. Extract Methods", desc: "Break the logic into small, private helper functions like 'calculateTotal' and 'applyDiscount'." },
        { step: "3. Rename for Intent", desc: "Change names like 'd' and 'amt' to 'isDiscountApplicable' and 'orderAmount'." },
        { step: "4. Introduce Guard Clauses", desc: "Remove the nested 'if/else' structure by exiting early if the order is invalid." },
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "goldenrod" }}>
                    PRACTICAL REFACTORING
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* ✅ The Challenge Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}><EngineeringIcon /></Avatar>
                            <Typography variant="h5" fontWeight={800} color="goldenrod">The Refactoring Loop</Typography>
                        </Box>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            We are going to refactor a <code>process()</code> function. In its current state, it is difficult to test, impossible to reuse, and hard to read. We will apply the <strong>Boy Scout Rule</strong>: leave the code better than we found it.
                        </Typography>



                        <Grid container spacing={3} sx={{ mt: 2 }}>
                            {refactorSteps.map((item, i) => (
                                <Grid item xs={12} md={6} key={i}>
                                    <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", height: "100%", borderLeft: "3px solid goldenrod" }}>
                                        <Typography variant="subtitle2" sx={{ color: "goldenrod", fontWeight: 800 }}>{item.step}</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.8 }}>{item.desc}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* ✅ Phase 1: The Dirty Code */}
                <Grid item xs={12} lg={6}>
                    <SectionCard sx={{ height: "100%" }}>
                        <Typography variant="h6" fontWeight={700} color="#ff6b6b" mb={2}>❌ The Dirty Code</Typography>
                        <Typography variant="body2" sx={{ mb: 2, opacity: 0.7 }}>
                            Cryptic names, magic numbers, and nested logic.
                        </Typography>
                        <CodeBlock color="#ff6b6b">
                            {`function proc(o) {
  if (o != null) {
    if (o.items.length > 0) {
      let t = 0;
      for (let i = 0; i < o.items.length; i++) {
        t += o.items[i].p;
      }
      if (t > 100) {
        t = t * 0.9;
      }
      console.log("Done: " + t);
      return t;
    } else {
      return 0;
    }
  }
}`}
                        </CodeBlock>
                    </SectionCard>
                </Grid>

                {/* ✅ Phase 2: The Clean Code */}
                <Grid item xs={12} lg={6}>
                    <SectionCard sx={{ height: "100%" }}>
                        <Typography variant="h6" fontWeight={700} color="#4ade80" mb={2}>✅ The Clean Code</Typography>
                        <Typography variant="body2" sx={{ mb: 2, opacity: 0.7 }}>
                            Self-documenting, flat, and modular.
                        </Typography>
                        <CodeBlock color="#4ade80">
                            {`const TAX_RATE = 0.10;
const DISCOUNT_THRESHOLD = 100;

const calculateTotal = (items) => 
  items.reduce((sum, item) => sum + item.price, 0);

const applyDiscount = (amount) => 
  amount > DISCOUNT_THRESHOLD ? amount * 0.9 : amount;

function processOrder(order) {
  // 1. Guard Clauses (Exit early)
  if (!order || order.items.length === 0) return 0;

  // 2. Clear Logic Steps
  const subtotal = calculateTotal(order.items);
  const finalTotal = applyDiscount(subtotal);

  // 3. Meaningful Logging
  logger.info(\`Order processed: \${finalTotal}\`);
  return finalTotal;
}`}
                        </CodeBlock>
                    </SectionCard>
                </Grid>

                {/* ✅ Summary of Improvements */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h6" fontWeight={700} color="goldenrod" mb={2}>Why is this better?</Typography>
                        <Stack spacing={2}>
                            <Box display="flex" gap={2}>
                                <CheckIcon sx={{ color: "goldenrod" }} />
                                <Typography variant="body2"><strong>Testability:</strong> We can now test <code>calculateTotal</code> without needing a full order object.</Typography>
                            </Box>
                            <Box display="flex" gap={2}>
                                <CheckIcon sx={{ color: "goldenrod" }} />
                                <Typography variant="body2"><strong>Readability:</strong> A non-developer can read <code>processOrder</code> and understand the business rules.</Typography>
                            </Box>
                            <Box display="flex" gap={2}>
                                <CheckIcon sx={{ color: "goldenrod" }} />
                                <Typography variant="body2"><strong>Maintainability:</strong> If the discount percentage changes, we only change one constant, not the whole logic block.</Typography>
                            </Box>
                        </Stack>
                    </SectionCard>
                </Grid>

                {/* ✅ Footer Navigation */}
                <Grid item xs={12} textAlign="center">
                    <Link to="/clean-code-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "goldenrod", color: "#100720", fontWeight: 800, px: 4, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Back to Top Button */}
            <Zoom in={true}>
                <Fab onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} size="small" sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}>
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
